/**
 * Data Quality Service — wraps /api/admin/ai/data-quality/ endpoints
 * Phase 4.8-B — WQ-200
 */

// === Types ===

export interface QualityCheck {
  id: string;
  check_type: string;
  column_name: string | null;
  passed: boolean;
  score: number;
  drift_detected: boolean;
  drift_statistic: number | null;
  drift_threshold: number | null;
  check_result: Record<string, unknown> | null;
  created_at: string;
}

export interface QualityProfile {
  id: string;
  catalog_entry_id: string;
  tenant_id: string;
  profile_type: string;
  dimensions: Record<string, number>;
  composite_score: number;
  row_count: number;
  schema_hash: string | null;
  is_baseline: boolean;
  created_at: string;
  checks: QualityCheck[];
}

export interface QualityScore {
  catalog_entry_id: string;
  quality_score: number;
}

export interface DriftAlert {
  catalog_entry_id: string;
  check_type: string;
  column_name: string | null;
  drift_statistic: number;
  detected_at: string;
}

export interface QualityTier {
  count: number;
  catalog_entry_ids: string[];
}

export interface MonitoringSummary {
  total_datasets: number;
  tiers: Record<string, QualityTier>;
  recent_drift_alerts: DriftAlert[];
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function runProfile(
  authFetch: FetchFn,
  body: { catalog_entry_id: string; set_baseline?: boolean; profile_type?: string },
): Promise<QualityProfile> {
  const res = await authFetch('/api/admin/ai/data-quality/profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to run quality profile: ${res.status}`);
  return res.json();
}

export async function listProfiles(
  authFetch: FetchFn,
  params?: { catalog_entry_id?: string; limit?: number; offset?: number },
): Promise<QualityProfile[]> {
  const qs = new URLSearchParams();
  if (params?.catalog_entry_id) qs.set('catalog_entry_id', params.catalog_entry_id);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/data-quality/profiles${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list profiles: ${res.status}`);
  return res.json();
}

export async function getLatestProfile(
  authFetch: FetchFn,
  catalogEntryId: string,
): Promise<QualityProfile> {
  const res = await authFetch(`/api/admin/ai/data-quality/profiles/latest/${catalogEntryId}`);
  if (!res.ok) throw new Error(`Failed to get latest profile: ${res.status}`);
  return res.json();
}

export async function checkDrift(
  authFetch: FetchFn,
  body: { catalog_entry_id: string },
): Promise<QualityProfile> {
  const res = await authFetch('/api/admin/ai/data-quality/drift-check', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to run drift check: ${res.status}`);
  return res.json();
}

export async function getScore(
  authFetch: FetchFn,
  catalogEntryId: string,
): Promise<QualityScore> {
  const res = await authFetch(`/api/admin/ai/data-quality/score/${catalogEntryId}`);
  if (!res.ok) throw new Error(`Failed to get quality score: ${res.status}`);
  return res.json();
}

export async function getSummary(authFetch: FetchFn): Promise<MonitoringSummary> {
  const res = await authFetch('/api/admin/ai/data-quality/summary');
  if (!res.ok) throw new Error(`Failed to get quality summary: ${res.status}`);
  return res.json();
}

export async function setBaseline(
  authFetch: FetchFn,
  catalogEntryId: string,
): Promise<QualityProfile> {
  const res = await authFetch(`/api/admin/ai/data-quality/baseline/${catalogEntryId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to set baseline: ${res.status}`);
  return res.json();
}
